/**
 * Product Card Block for Editor.js
 * Creates email-safe product cards for featuring wines/beers
 */

import { BlockTool } from '@editorjs/editorjs';

export interface ProductBlockData {
  imageUrl: string;
  name: string;
  producer: string;
  description: string;
  price: string;
  buttonText: string;
  buttonUrl: string;
}

export default class ProductBlock implements BlockTool {
  private data: ProductBlockData;
  private wrapper: HTMLElement | null = null;

  static get toolbox() {
    return {
      title: 'Product Card',
      icon: '<svg width="17" height="15" viewBox="0 0 17 15" xmlns="http://www.w3.org/2000/svg"><rect x="1" y="1" width="6" height="13" rx="1" stroke="currentColor" stroke-width="1.5" fill="none"/><path d="M10 3h6M10 6h5M10 9h4M10 12h3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    };
  }

  constructor({ data }: { data: ProductBlockData }) {
    this.data = {
      imageUrl: data.imageUrl || 'https://res.cloudinary.com/dcyi0j3ky/image/upload/v1/email-campaigns/sample-wine.jpg',
      name: data.name || 'Product Name',
      producer: data.producer || 'Producer or Region',
      description: data.description || 'Product description goes here. Highlight key features and tasting notes.',
      price: data.price || '$29.99',
      buttonText: data.buttonText || 'View Product',
      buttonUrl: data.buttonUrl || 'https://www.ejswineshop.com',
    };
  }

  render(): HTMLElement {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('email-product-block');
    this.wrapper.style.cssText = `
      display: flex;
      padding: 24px 0;
      border-bottom: 1px solid #e5e5e5;
      margin: 8px 0;
      gap: 24px;
    `;

    // Image container
    const imageContainer = document.createElement('div');
    imageContainer.style.cssText = 'flex-shrink: 0; width: 150px;';

    const image = document.createElement('img');
    image.src = this.data.imageUrl;
    image.alt = this.data.name;
    image.style.cssText = 'width: 150px; height: auto; border-radius: 4px; cursor: pointer;';
    image.addEventListener('click', () => this.openImageUpload());
    imageContainer.appendChild(image);

    // Content container
    const content = document.createElement('div');
    content.style.cssText = 'flex: 1;';

    // Product name
    const name = document.createElement('h3');
    name.contentEditable = 'true';
    name.textContent = this.data.name;
    name.style.cssText = `
      font-size: 18px;
      font-weight: 600;
      color: #2b2b2b;
      margin: 0 0 8px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      outline: none;
    `;
    name.addEventListener('input', () => {
      this.data.name = name.textContent || '';
    });

    // Producer
    const producer = document.createElement('p');
    producer.contentEditable = 'true';
    producer.textContent = this.data.producer;
    producer.style.cssText = `
      font-size: 14px;
      color: #666666;
      margin: 0 0 4px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      outline: none;
    `;
    producer.addEventListener('input', () => {
      this.data.producer = producer.textContent || '';
    });

    // Description
    const description = document.createElement('p');
    description.contentEditable = 'true';
    description.textContent = this.data.description;
    description.style.cssText = `
      font-size: 14px;
      line-height: 22px;
      color: #666666;
      margin: 0 0 12px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      outline: none;
    `;
    description.addEventListener('input', () => {
      this.data.description = description.textContent || '';
    });

    // Price
    const price = document.createElement('p');
    price.contentEditable = 'true';
    price.textContent = this.data.price;
    price.style.cssText = `
      font-size: 20px;
      font-weight: 700;
      color: #722f37;
      margin: 0 0 16px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      outline: none;
    `;
    price.addEventListener('input', () => {
      this.data.price = price.textContent || '';
    });

    // Button
    const button = document.createElement('a');
    button.href = this.data.buttonUrl;
    button.contentEditable = 'true';
    button.textContent = this.data.buttonText;
    button.style.cssText = `
      background-color: #D4A657;
      color: #ffffff;
      font-size: 14px;
      font-weight: 600;
      padding: 10px 24px;
      border-radius: 4px;
      text-decoration: none;
      display: inline-block;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      outline: none;
    `;
    button.addEventListener('input', () => {
      this.data.buttonText = button.textContent || '';
    });
    button.addEventListener('click', (e) => e.preventDefault());

    content.appendChild(name);
    content.appendChild(producer);
    content.appendChild(description);
    content.appendChild(price);
    content.appendChild(button);

    this.wrapper.appendChild(imageContainer);
    this.wrapper.appendChild(content);

    return this.wrapper;
  }

  private openImageUpload(): void {
    // @ts-ignore - Cloudinary widget
    if (!window.cloudinary) {
      const url = prompt('Enter image URL:', this.data.imageUrl);
      if (url) {
        this.data.imageUrl = url;
        const img = this.wrapper?.querySelector('img');
        if (img) img.src = url;
      }
      return;
    }

    // @ts-ignore
    const widget = window.cloudinary.createUploadWidget(
      {
        cloudName: 'dcyi0j3ky',
        uploadPreset: 'email_images',
        sources: ['local', 'url', 'camera'],
        multiple: false,
        resourceType: 'image',
        folder: 'email-campaigns',
      },
      (error: any, result: any) => {
        if (!error && result && result.event === 'success') {
          this.data.imageUrl = result.info.secure_url;
          const img = this.wrapper?.querySelector('img');
          if (img) img.src = result.info.secure_url;
          widget.close();
        }
      }
    );
    widget.open();
  }

  save(): ProductBlockData {
    return this.data;
  }

  renderSettings(): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.style.padding = '8px';

    // Image URL
    const imageLabel = document.createElement('label');
    imageLabel.textContent = 'Image URL:';
    imageLabel.style.cssText = 'display: block; margin-bottom: 4px; font-size: 12px; color: #666;';

    const imageInput = document.createElement('input');
    imageInput.type = 'url';
    imageInput.value = this.data.imageUrl;
    imageInput.placeholder = 'https://...';
    imageInput.style.cssText = 'width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; margin-bottom: 12px;';
    imageInput.addEventListener('change', () => {
      this.data.imageUrl = imageInput.value;
      const img = this.wrapper?.querySelector('img');
      if (img) img.src = imageInput.value;
    });

    // Button URL
    const urlLabel = document.createElement('label');
    urlLabel.textContent = 'Button URL:';
    urlLabel.style.cssText = 'display: block; margin-bottom: 4px; font-size: 12px; color: #666;';

    const urlInput = document.createElement('input');
    urlInput.type = 'url';
    urlInput.value = this.data.buttonUrl;
    urlInput.placeholder = 'https://...';
    urlInput.style.cssText = 'width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px;';
    urlInput.addEventListener('change', () => {
      this.data.buttonUrl = urlInput.value;
    });

    wrapper.appendChild(imageLabel);
    wrapper.appendChild(imageInput);
    wrapper.appendChild(urlLabel);
    wrapper.appendChild(urlInput);

    return wrapper;
  }
}
